<template>
  <no-auth v-if="!hasAuthorize.list"></no-auth>
  <div :class="{ 'o-card': !tab }" v-else>
    <Table
      no-card
      :title="$t(`${name}.title`)"
      :description="$t(`${name}.description`)"
      i18n
      :status="getStatus"
      :loading="getItemsLoading"
      :items="getItems(page)"
      checkable
      :checkValue="checkedList"
      @change:sort="SET_CHANGE_SORT"
      @change:checkedList="checkedList = $event"
      @change:selectAllWithoutPage="SET_CHECKED_ALL"
      :headers="headers"
      :page="page"
      :count="getItemCount"
      :pages="getItemPages"
      :searchValue="search"
      @change:search="setSearch"
      @change:page="setChangedPage"
      :searchPlaceholder="$t(`${name}.searchPlaceholder`)"
    >
      <template #new-button>
        <div class="u-display-flex u-width-100% u-justify-content-flex-end">
          <ExcelExport
            :fileName="`${name}-export-${date()}`"
            :fields="exportFields"
            :data="checkedList.length > 0 ? checkedList : getItems()"
          >
            <template #button="{ showExcell }">
              <Button
                @click="showExcell()"
                variant="light-success"
                class="u-margin-right-xsmall"
              >
                <i class="icofont-arrow-up"></i> {{ $t("common.export") }}
              </Button>
            </template>
          </ExcelExport>

          <router-link v-if="!tab" :to="{ name: `new-${name}` }">
            <Button variant="light-primary"> + {{ $t(`${name}.add`) }} </Button>
          </router-link>
        </div>
      </template>
      <template #filter v-if="!noFilter">
        <BatteryFilter @change:filter="setFilter"></BatteryFilter>
      </template>
      <template #name="{ item }">
        <div class="u-cursor-pointer" @click="showDetail(item)">
          <img
            v-tooltip="item.type"
            :src="require(`@/assets/images/battery-types/${item.type}.png`)"
            :alt="item.name"
            width="25"
          />
          <span class="u-margin-left-xsmall">
            {{ item.name }}
          </span>
        </div>
      </template>
      <template #type="{ item }">
        <div>
          <Chip size="sm" :variant="getBatteryType(item.type).variant">
            {{ $t(item.type) }}
          </Chip>
        </div>
      </template>
      <template #createdDate="{ item }">
        <div>
          {{ date(item.createdAt) }}
        </div>
      </template>
      <template #userInfo="{ item }">
        <div>
          <Chip :variant="item.isLocked ? 'danger' : 'success'">
            <i :class="`icofont-ui-${item.isLocked ? '' : 'un'}lock`"></i>
            {{
              item.isLocked
                ? $t("common.lockedText")
                : $t("common.unlockedText")
            }}
          </Chip>
        </div>
        <div class="u-cursor-pointer" @click="showAdminDetail(item)">
          <small>
            <i class="icofont-user"></i> {{ item.userInfo.fullname }}</small
          >
        </div>
      </template>
      <template #action="{ item }">
        <div
          class="u-display-flex u-align-items-center u-justify-content-flex-end"
        >
          <Dropdown align="right">
            <template #title>
              <div class="u-font-size-xlarge u-padding-left">
                <i class="icofont-bubble-down"></i>
              </div>
            </template>
            <template #content>
              <ul class="c-list">
                <li @click="printPdf(item)" class="c-list__item">
                  <span>
                    <i class="icofont-print u-margin-right-xsmall"></i>
                    {{ $t("common.print") }}</span
                  >
                </li>
                <li
                  v-if="hasAuthorize.locked"
                  @click="LOCKED_TO(item)"
                  class="c-list__item"
                  :class="{ 'c-list__item--disabled': !item.isPermission }"
                >
                  <span>
                    <i class="icofont-ui-lock u-margin-right-xsmall"></i>
                    {{ $t(`common.${item.isLocked ? "un" : ""}locked`) }}</span
                  >
                  <i class="icofont-ui-lock" v-if="!item.isPermission"></i>
                </li>
                <router-link
                  :to="{ name: `${name}-copy`, params: { id: item.id } }"
                >
                  <li class="c-list__item">
                    <span>
                      <i class="icofont-ui-copy u-margin-right-xsmall"></i>
                      {{ $t("common.copy") }}</span
                    >
                  </li></router-link
                >
                <component
                  v-if="hasAuthorize.edit"
                  :is="
                    item.isPermission && !item.isLocked ? 'router-link' : 'span'
                  "
                  :to="{ name: `${name}-edit`, params: { id: item.id } }"
                >
                  <li
                    class="c-list__item"
                    :class="{
                      'c-list__item--disabled':
                        !item.isPermission || item.isLocked,
                    }"
                  >
                    <span>
                      <i class="icofont-edit u-margin-right-xsmall"></i>
                      {{ $t("common.edit") }}</span
                    >
                    <i
                      class="icofont-ui-lock"
                      v-if="!item.isPermission || item.isLocked"
                    ></i>
                  </li>
                </component>
                <router-link
                  v-if="hasAuthorize.getById"
                  :to="{ name: `${name}-detail`, params: { id: item.id } }"
                >
                  <li class="c-list__item">
                    <span>
                      <i class="icofont-eye u-margin-right-xsmall"></i>
                      {{ $t("common.goToDetail") }}</span
                    >
                  </li>
                </router-link>
                <li
                  v-if="hasAuthorize.delete"
                  @click="REMOVE_TO(item)"
                  class="c-list__item"
                  :class="{
                    'c-list__item--disabled':
                      !item.isPermission || item.isLocked,
                  }"
                >
                  <span>
                    <i class="icofont-trash u-margin-right-xsmall"></i>
                    {{ $t("common.remove") }}</span
                  >
                  <i
                    class="icofont-ui-lock"
                    v-if="!item.isPermission || item.isLocked"
                  ></i>
                </li>
              </ul>
            </template>
          </Dropdown>
        </div>
      </template>
    </Table>
    <LockedModal
      v-model="lockedModal.status"
      :description="$t(`${update.title}.${SET_LOCKED_DESCRIPTION()}`)"
      :okText="$t(`common.${SET_LOCKED_DESCRIPTION()}`)"
      @ok="LOCKED_ITEM"
    ></LockedModal>
    <RemoveModal @ok="REMOVE_ITEM" v-model="removeModal.status"></RemoveModal>
    <drawer
      @click:title="
        $router.push({
          name: 'batteries-detail',
          params: { id: showDetailModal?.item?.id },
        })
      "
      :title="showDetailModal?.item?.name"
      v-model="showDetailModal.status"
      size="large"
    >
      <div v-if="showDetailModal.item">
        <BatteryDrawerDetail
          no-header
          :batteryId="showDetailModal?.item?.id"
          :battery="showDetailModal?.item"
        ></BatteryDrawerDetail>
      </div>
    </drawer>
    <drawer v-model="showDetailModal.adminDetailStatus">
      <div v-if="showDetailModal.item">
        <AdminDrawerDetail
          :admin-id="showDetailModal?.item?.userInfo.id"
        ></AdminDrawerDetail>
      </div>
    </drawer>
    <BatteryInfoPdf
      v-if="printing.status"
      @pdf-created-link="pdfCreatedLink"
      :project="{ evaporatorBatteryId: printing.item }"
    ></BatteryInfoPdf>
  </div>
</template>

<script>
import Table from "@/components/Base/Table/index.vue";
import DateHelper from "@/helpers/date";
import { mapGetters } from "vuex";
import Button from "../Base/Button.vue";
import Chip from "../Base/Chip.vue";
import Dropdown from "../Base/Dropdown.vue";
import LockedModal from "@/components/Shared/LockedModal/index.vue";
import RemoveModal from "@/components/Shared/RemoveModal/index.vue";
import { BatteryStatus } from "@/enums/Battery/BatteryStatus";
import { getBatteryType } from "@/helpers/Battery/getBatteryType";
import { getBatteryStatus } from "@/helpers/Battery/getBatteryStatus";
import BatteryDrawerDetail from "./BatteryDrawerDetail.vue";
import Drawer from "../Base/Drawer.vue";
import BatteryFilter from "./BatteryFilter.vue";
import ExcelExport from "../Base/ExcelExport.vue";
import Requests from "@/services/Requests";
import batteryExportFields from "./batteryExportFields";
import AdminDrawerDetail from "../Admins/AdminDrawerDetail.vue";
import authorize from "./authorize";
import BatteryInfoPdf from "../FileManager/Pdfs/EvaporatorBatteryInfo.vue";
export default {
  mixins: [authorize, batteryExportFields],
  components: {
    Drawer,
    Dropdown,
    LockedModal,
    BatteryDrawerDetail,
    RemoveModal,
    BatteryFilter,
    Chip,
    Table,
    Button,
    ExcelExport,
    AdminDrawerDetail,
    BatteryInfoPdf,
  },
  props: {
    query: {
      type: Object,
      default: () => ({}),
    },
    noFilter: {
      type: Boolean,
      default: false,
    },
    tab: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      name: "batteries",
      checkedList: [],
      showLockedModal: false,
      showRemoveModal: false,
      filter: {},
      sort: {},
      get: {
        title: "batteries",
        dispatch: "getBatteries",
      },
      update: {
        title: "updateBatteries",
        dispatch: "updateBattery",
        lockedStatus: BatteryStatus.Locked,
      },
      remove: {
        title: "removeBatteries",
        dispatch: "removeBattery",
      },
      page: 1,
      search: "",
      headers: [
        {
          col: 10,
          sm: 3,
          md: 4,
          lg: 3,
          title: "batteries.name",
          value: "name",
          sortable: true,
        },
        {
          col: 4,
          sm: 2,
          md: 4,
          lg: 2,
          title: "batteries.type",
          value: "type",
        },
        {
          col: 5,
          sm: 2,
          md: 4,
          lg: 2,
          title: "common.createdDate",
          value: "createdDate",
          sortable: true,
        },
        {
          col: 10,
          sm: 2,
          md: 4,
          lg: 2,
          title: "common.userInfo",
          value: "userInfo",
        },
        {
          col: 2,
          sm: 1,
          md: 4,
          lg: 1,
          align: "right",
          title: "common.action",
          value: "action",
        },
      ],
      removeModal: { status: false, item: {} },
      lockedModal: { status: false, item: {} },
      printing: { status: false, item: {} },
      showDetailModal: { status: false, adminDetailStatus: false, item: null },
    };
  },
  computed: {
    ...mapGetters({
      getItems: "getBatteries",
      getItemsLoading: "getBatteriesLoading",
      getItemPages: "getBatteryPages",
      getItemCount: "getBatteryCount",
      getActiveUser: "getActiveUser",
      getStatus: "getBatteriesStatus",
    }),
    exportFields() {
      if (this.getItems().length === 0) return {};
      return this.getExcelFields();
    },
  },
  methods: {
    getBatteryType,
    getBatteryStatus,
    pdfCreatedLink(url) {
      window.open(url, "_blank");
      this.printing = { status: false, item: {} };
    },
    printPdf(item) {
      this.printing = { status: true, item: item };
    },
    SET_CHANGE_SORT(headers) {
      const sortedHeaders = headers.filter((item) => item.sort);
      if (sortedHeaders.length === 0) {
        this.sort = {};
        this.getItemsOfPage();
      } else {
        const sort = sortedHeaders[0];
        if (sort.sort.includes("name")) {
          const value = sort.sort.split("-")[1];
          this.sort = {
            sort: value.toUpperCase(),
            sortFilter: "ALPHABETIC",
          };
        } else if (sort.sort.includes("createdDate")) {
          const value = sort.sort.split("-")[1];
          this.sort = {
            sort: value.toUpperCase(),
            sortFilter: "DATE",
          };
        }
        this.getItemsOfPage();
      }
    },
    async SET_CHECKED_ALL(data) {
      if (data) {
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: true,
          status: true,
        });
        const response = await Requests.Battery.GetAll({
          query: {
            page: 1,
            limit: this.getItemCount,
            search: this.search,
            ...this.query,
          },
        });
        if (response.message === "OK") {
          console.log(response.data);

          this.checkedList = [...response.data.items];
        }
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: false,
        });
      }
    },
    showDetail(item) {
      this.showDetailModal = {
        status: true,
        adminDetailStatus: false,
        item: item,
      };
    },
    showAdminDetail(item) {
      this.showDetailModal = {
        status: false,
        adminDetailStatus: true,
        item: item,
      };
    },
    date(value = new Date()) {
      const date = new DateHelper(this.$i18n.locale);
      return date.format(value);
    },
    SET_LOCKED_DESCRIPTION() {
      return this.lockedModal.item.isLocked ? "unlocked" : "locked";
    },
    REMOVE_TO(item) {
      if (!item.isPermission || item.isLocked) return;

      this.removeModal = {
        status: true,
        item: item,
      };
    },
    async REMOVE_ITEM(loading) {
      loading(true);

      const response = await this.$store.dispatch(this.remove.dispatch, {
        id: this.removeModal.item.id,
      });
      if (response.message === "OK") {
        this.getItemsOfPage();
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.remove.title}.success`),
          variant: "success",
          duration: 2500,
        });
      } else {
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.remove.title}.error`),
          variant: "danger",
          duration: 2500,
        });
      }
      loading(false);
    },
    LOCKED_TO(item) {
      if (!item.isPermission) return;
      this.lockedModal = {
        status: true,
        item: item,
      };
    },
    async LOCKED_ITEM(loading) {
      loading(true);
      const response = await Requests.Battery.UpdateLockStatus(
        { params: { id: this.lockedModal.item.id } },
        { isLocked: !this.lockedModal.item.isLocked }
      );
      if (response.message === "OK") {
        this.getItemsOfPage();
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.update.title}.success`),
          variant: "success",
          duration: 2500,
        });
      } else {
        this.$store.dispatch("SET_ACTION_MODAL", {
          isLoading: false,
          status: true,
          message: this.$t(`${this.update.title}.error`),
          variant: "danger",
          duration: 2500,
        });
      }
      loading(false);
    },
    setQuery() {
      const page = Number(this.$route.query.page);
      const search = this.$route.query.search;
      if (!isNaN(page)) this.page = page;
      if (search) this.search = search;
      this.getItemsOfPage();
    },
    getItemsOfPage() {
      this.removeModal = { status: false, item: {} };
      this.lockedModal = { status: false, item: {} };
      const query = {
        limit: 10,
        page: this.page,
        search: this.search,
        ...this.filter,
        ...this.sort,
        ...this.query,
      };
      this.$store.dispatch(this.get.dispatch, {
        query: query,
        force: true,
      });
      this.$router.replace({ query: { ...this.$route.query, ...query } });
    },
    setSearch(search) {
      this.search = search;
      this.page = 1;
      this.$router.replace({
        query: { ...this.$route.query, search: this.search, page: 1 },
      });
      this.getItemsOfPage();
    },
    setChangedPage(page) {
      this.$router.replace({
        query: { ...this.$route.query, page: page },
      });
      this.page = page;
      this.getItemsOfPage();
    },
    setFilter(filter) {
      this.page = 1;
      const filterScoped = {
        type: filter.type || "",
        isLocked:
          filter.isLocked == null
            ? ""
            : filter.isLocked
            ? "true"
            : "false" || "",
        userId: filter.user?.id || "",
        projectId: filter.projectId?.id || "",
      };
      // remove
      this.filter = filterScoped;
      this.getItemsOfPage(filterScoped);
    },
  },
  mounted() {
    if (this.query) {
      this.getItemsOfPage();
    }
  },
};
</script>

<style></style>
